import { LOW_STOCK_THRESHOLD, loadCatalog } from "./catalog";

const STORAGE_KEY = "almoune_notifications";
const DISMISSED_KEY = "almoune_notifications_dismissed";

const normalizeNotification = (n) => ({
  id: n.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  type: n.type || "stock_alert",
  severity: n.severity || "warning",
  title: n.title || "",
  message: n.message || "",
  productId: n.productId || null,
  productName: n.productName || "",
  currentStock: Number(n.currentStock || 0),
  read: Boolean(n.read),
  createdAt: n.createdAt || new Date().toISOString(),
});

const readCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeNotification) : [];
  } catch {
    return [];
  }
};

const writeCache = (notifications) => {
  const normalized = notifications.map(normalizeNotification);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

const readDismissed = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeDismissed = (ids) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  }
};

export const getNotifications = () => readCache();

export const getUnreadCount = () => readCache().filter((n) => !n.read).length;

export const markAsRead = (id) => {
  const all = readCache();
  const updated = all.map((n) => n.id === id ? { ...n, read: true } : n);
  writeCache(updated);
  return updated;
};

export const markAllAsRead = () => {
  const all = readCache();
  const updated = all.map((n) => ({ ...n, read: true }));
  writeCache(updated);
  return updated;
};

export const dismissNotification = (id) => {
  const all = readCache();
  const updated = all.filter((n) => n.id !== id);
  writeCache(updated);
  const dismissed = readDismissed();
  writeDismissed([...dismissed, id]);
  return updated;
};

export const clearAllNotifications = () => {
  writeCache([]);
  return [];
};

/**
 * Scan the catalog and generate stock alert notifications.
 * Returns the updated notification list.
 */
export const generateStockAlerts = async () => {
  const products = await loadCatalog();
  const existing = readCache();
  const dismissed = readDismissed();
  const existingProductIds = new Set(existing.map((n) => n.productId).filter(Boolean));

  const newNotifications = [];

  for (const product of products) {
    const stock = Number(product.stock || 0);
    if (stock > LOW_STOCK_THRESHOLD) continue;
    if (dismissed.includes(`stock-${product.id}`)) continue;

    const isExisting = existing.some((n) => n.productId === product.id && n.type === "stock_alert");
    if (isExisting) continue;

    const severity = stock <= 0 ? "danger" : stock <= 5 ? "critical" : "warning";
    const title = stock <= 0
      ? `Rupture de stock: ${product.name}`
      : `Stock faible: ${product.name}`;
    const message = stock <= 0
      ? `${product.name} est en rupture de stock. Reapprovisionnement urgent requis.`
      : `Il ne reste que ${stock} unite(s) de ${product.name}. Seuil alerte: ${LOW_STOCK_THRESHOLD}.`;

    newNotifications.push(normalizeNotification({
      id: `stock-alert-${product.id}-${Date.now()}`,
      type: "stock_alert",
      severity,
      title,
      message,
      productId: product.id,
      productName: product.name,
      currentStock: stock,
      read: false,
      createdAt: new Date().toISOString(),
    }));
  }

  if (newNotifications.length > 0) {
    const updated = [...newNotifications, ...existing].slice(0, 100);
    writeCache(updated);
    return updated;
  }

  return existing;
};

/**
 * Get low stock products for display.
 */
export const getLowStockProducts = async () => {
  const products = await loadCatalog();
  return products
    .filter((p) => Number(p.stock || 0) <= LOW_STOCK_THRESHOLD)
    .sort((a, b) => Number(a.stock || 0) - Number(b.stock || 0));
};
